/**
 * MODAL — with the rule enforced in code, not in a review comment. 09 §2.
 *
 *   "A modal is a short action: edit one thing · approve · confirm · add a note.
 *    A modal is not a substitute for a child screen."
 *
 * The six tests (09 §2.1). Fail any one → it is a child screen with an address:
 *   M1 it changes state (it is an action, not a view)          — `intent` is required and is a verb
 *   M2 it fits on one screen without internal scroll           — ≤ 6 fields, ENFORCED below
 *   M3 it is one decision, not a workflow                      — no steps, no wizard
 *   M4 it contains no table the user must read row by row      — ENFORCED below
 *   M5 losing it costs nothing that cannot be retyped in 30s   — Esc always closes
 *   M6 it has no drill-down of its own                         — no links out, ENFORCED below
 *
 * The invariant throws in dev and is a no-op in production: a customer must never see a developer's
 * error, and a developer must never ship a modal that is really a screen (09 §5.8, §9.1 V3).
 */

import { Children, isValidElement, useEffect } from 'react';
import type { ReactElement, ReactNode } from 'react';

/** 09 §2.1 M2 — "fits on one screen without its own internal scroll, ≤ ~6 fields". */
export const MAX_MODAL_FIELDS = 6;

export class ModalInvariantError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ModalInvariantError';
  }
}

/** Element types that make a modal a child screen instead. */
const TABLE_TAGS: ReadonlySet<string> = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td']);
const TABLE_COMPONENTS: ReadonlySet<string> = new Set(['DataTable', 'BlockedReasons']);

function componentName(type: unknown): string | null {
  if (typeof type === 'function') {
    const named = type as { displayName?: string; name?: string };
    return named.displayName ?? named.name ?? null;
  }
  return null;
}

/**
 * Walks the modal's children looking for the two things §2.1 forbids outright.
 * Exported so the invariant can be unit-tested without a DOM.
 */
export function assertModalInvariants(fieldCount: number, children: ReactNode): void {
  if (fieldCount > MAX_MODAL_FIELDS) {
    throw new ModalInvariantError(
      `09 §2.1 M2: a modal holds at most ${MAX_MODAL_FIELDS} fields; this one declares ${fieldCount}. ` +
        'It is a child screen with an address, not a modal.',
    );
  }

  const visit = (node: ReactNode): void => {
    Children.forEach(node, (child) => {
      if (!isValidElement(child)) return;
      const el = child as ReactElement<{ children?: ReactNode }>;

      if (typeof el.type === 'string' && TABLE_TAGS.has(el.type)) {
        throw new ModalInvariantError(
          `09 §2.1 M4: a modal contains no table the user must read row by row (found <${el.type}>). ` +
            'It is a child screen with an address, not a modal.',
        );
      }
      const name = componentName(el.type);
      if (name !== null && TABLE_COMPONENTS.has(name)) {
        throw new ModalInvariantError(
          `09 §2.1 M4: a modal contains no table the user must read row by row (found <${name}>). ` +
            'It is a child screen with an address, not a modal.',
        );
      }
      if (typeof el.type === 'string' && el.type === 'a') {
        throw new ModalInvariantError(
          '09 §2.1 M6: a modal has no drill-down of its own (found a link). ' +
            'A modal never opens another place — that place is a child screen with an address.',
        );
      }

      visit(el.props.children);
    });
  };

  visit(children);
}

export interface ModalProps {
  /**
   * M1 — a verb, in business Hebrew. "אישור" / "נעילת שנה" / "הוספת הערה".
   * A noun here means it is a screen (09 §1.3: a noun is a screen, a verb is an action).
   */
  readonly intentHe: string;
  /**
   * 09 §2.2: "the modal states plainly what will happen, including the company and period it
   * applies to. An external customer must not have to infer the blast radius."
   */
  readonly blastRadiusHe: string;
  /** M2 — the declared field count. The invariant reads this, not the DOM. */
  readonly fields: number;
  /**
   * 09 §2.2: an irreversible action requires an explicit re-selection, never a bare OK,
   * and (09 §9.5 A7) is never the default focus.
   */
  readonly irreversible?: boolean;
  readonly confirmLabelHe: string;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
  readonly children: ReactNode;
}

export function Modal(props: ModalProps): JSX.Element {
  const { intentHe, blastRadiusHe, fields, irreversible = false, confirmLabelHe, onConfirm, onClose, children } =
    props;

  // Dev-only. A production customer never sees a developer's error (09 §9.1 V3); the check is a
  // build-time discipline expressed at runtime, and it is stripped from the production bundle.
  if (import.meta.env.DEV) {
    assertModalInvariants(fields, children);
  }

  // M5 — losing it (Esc, click-away) costs nothing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-scrim" onClick={onClose} role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={intentHe}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="modal__intent">{intentHe}</h2>
        <p className="modal__blast-radius">{blastRadiusHe}</p>
        <div className="modal__fields">{children}</div>
        <div className="modal__actions">
          {/* A7: the destructive action is never the default focus, so cancel comes first in DOM order. */}
          <button type="button" className="btn" onClick={onClose} autoFocus>
            ביטול
          </button>
          <button
            type="button"
            className={irreversible ? 'btn btn--irreversible' : 'btn btn--primary'}
            onClick={onConfirm}
          >
            {confirmLabelHe}
          </button>
        </div>
      </div>
    </div>
  );
}
